<template>
  <mi-expansion-item
    :class="`summary-collection--${ color }`"
    class="summary-collection relative-position overflow-auto q-mt-md"
    expand-icon-class="summary-collection__expand-icon"
    header-class="summary-collection__header text-weight-bold"
    default-opened
  >
    <!-- Header -->
    <template #header>
      <mi-list-item-section class="summary-collection__label items-center">
        <slot name="header"></slot>

        <!-- Collapse / Expand all button -->
        <mi-btn
          v-if="items.length"
          :ripple="false"
          class="summary-collection__collapse-btn q-ml-auto"
          icon-size="12px"
          dense
          flat
          icon-type
          fab
          @click.stop="toggleAllItemsCollapse"
        >
          <img v-if="!areItemsCollapsed" src="@/assets/images/vm/collapse.svg" alt="collapse" />
          <img v-else src="@/assets/images/vm/expand.svg" alt="collapse" />
        </mi-btn>

        <!-- Collapse / Expand all button disabled -->
        <mi-btn
          v-else
          :ripple="false"
          class="summary-collection__collapse-btn q-ml-auto"
          icon-size="12px"
          dense
          flat
          icon-type
          fab
          @click.stop="toggleAllItemsCollapse"
        >
          <img v-if="!areItemsCollapsed" src="@/assets/images/vm/collapse-grey.svg" alt="collapse" />
          <img v-else src="@/assets/images/vm/expand-grey.svg" alt="collapse" />
        </mi-btn>

        <!-- Clear button -->
        <mi-btn
          :ripple="false"
          class="summary-collection__clear-btn"
          :class="{ 'summary-collection__clear-btn-disabled' : !items.length }"
          icon="trash"
          :disable="!items.length"
          icon-size="12px"
          dense
          flat
          icon-type
          fab
          @click.stop="handleClearButtonClick"
        >
          <mi-tooltip anchor="top middle" self="bottom middle"> Clear collection </mi-tooltip>
        </mi-btn>
      </mi-list-item-section>
    </template>

    <!-- Content -->
    <div v-show="items.length" class="summary-collection__content q-pt-sm q-pb-md">
      <mi-list>
        <transition-group name="collapse-item">
          <search-results-group
            v-for="item in items"
            :key="item.id"
            :item="item"
            :color="color"
            persistent-title
            removable
            :opened="openedItems[item.id]"
            :selected-elements-ids="selectedElementsIds"
            :handle-item-click="handleItemClick"
            :total-sub-elements-counter="totalSubElementsCounters.find(
              value => value.id2 === item.id2
            )?.totalSubElements"
            :handle-sub-item-click="handleSubItemClick"
            :add-all-options="addAllOptions"
            :are-all-options-loading="areAllOptionsLoading"
            @update:opened="handleItemOpenedUpdate"
          ></search-results-group>
        </transition-group>
      </mi-list>
    </div>
    <slot name="footer"></slot>
  </mi-expansion-item>
</template>

<script>
  import { createNamespacedHelpers, mapState } from 'vuex'
  import recordAnalytics from '@/utils/analytics/recordAnalytics'
  import { ELEMENT_COLORS, ELEMENT_STATES, SEARCH_RESULT_TYPES } from '@/constants'
  import {
    CLEAR_SELECTED_ELEMENTS,
    TOGGLE_ELEMENT_STATE,
    TOGGLE_SUB_ELEMENT_STATE
  } from '@/store/modules/search/mutationTypes'

  import { VM_CLEARED_COLLECTION } from '@/utils/analytics/constants'
  import SearchResultsGroup from '../search-results/SearchResultsGroup.vue'

  const { mapMutations } = createNamespacedHelpers('search')

  export default {
    name: 'SearchSummaryCollection',
    components: { SearchResultsGroup },
    props: {
      color: {
        type: String,
        required: false,
        default: '',
        validator: value => Object.values(ELEMENT_COLORS).includes(value) || !value
      },
      items: {
        type: Array,
        required: false,
        default: () => []
      },
      addAllOptions: {
        type: Function,
        required: false,
        default: () => {}
      },
      totalSubElementsCounters: {
        type: Array,
        required: false,
        default: () => []
      },
      areAllOptionsLoading: {
        type: Boolean,
        required: false,
        default: false
      }
    },
    emits: ['visible-items-count-updated'],
    data() {
      return {
        openedItems: {}
      }
    },
    computed: {
      ...mapState('search', ['selectedElementsIds']),
      areItemsCollapsed() {
        return this.items.length > 0 && this.items.every(item => !(this.openedItems[item.id] ?? true))
      },
      visibleItemsCount() {
        return this.items.reduce((total, item) => {
          const isOpened = this.openedItems[item.id] ?? true
          const subItemsCount = this.getSubItemsCount(item)

          return total + 1 + (isOpened ? subItemsCount : 0)
        }, 0)
      }
    },
    watch: {
      items: {
        immediate: true,
        handler(items) {
          this.openedItems = items.reduce((acc, item) => {
            acc[item.id] = this.openedItems[item.id] ?? true
            return acc
          }, {})

          this.$nextTick(() => {
            this.emitVisibleItemsCount()
          })
        }
      },
      visibleItemsCount: {
        handler() {
          this.emitVisibleItemsCount()
        }
      }
    },
    methods: {
      ...mapMutations({ CLEAR_SELECTED_ELEMENTS, TOGGLE_ELEMENT_STATE, TOGGLE_SUB_ELEMENT_STATE }),
      emitVisibleItemsCount() {
        this.$emit('visible-items-count-updated', this.visibleItemsCount)
      },
      getSubItemsCount(item = {}) {
        if (this.color === ELEMENT_COLORS.YELLOW) {
          return item.choices?.length || 0
        }

        if (this.color === ELEMENT_COLORS.GREEN) {
          return item.componentVariants?.length || 0
        }

        return 0
      },
      toggleAllItemsCollapse() {
        const shouldOpenAll = this.areItemsCollapsed

        this.openedItems = this.items.reduce((acc, item) => {
          acc[item.id] = shouldOpenAll
          return acc
        }, {})
      },
      handleClearButtonClick() {
        let currentType = ''

        if (this.color === ELEMENT_COLORS.YELLOW) {
          currentType = SEARCH_RESULT_TYPES.OPTIONS
        }
        else if (this.color === ELEMENT_COLORS.GREEN) {
          currentType = SEARCH_RESULT_TYPES.COMPONENTS
        }

        this.CLEAR_SELECTED_ELEMENTS(currentType)

        // Analytics
        recordAnalytics(
          VM_CLEARED_COLLECTION,
          { type: currentType }
        )
      },
      handleItemClick(elementTypes, { item = {}, isChosen = false } = {}) {
        this.TOGGLE_ELEMENT_STATE({
          element: item,
          elementsType: elementTypes.item,
          elementState: ELEMENT_STATES.SELECTED,
          subElementsType: elementTypes.subItem,
          isChosen
        })
      },
      handleSubItemClick(elementTypes, { subItem = {}, isChosen = false } = {}) {
        this.TOGGLE_SUB_ELEMENT_STATE({
          subElement: subItem,
          subElementsType: elementTypes.subItem,
          subElementState: ELEMENT_STATES.SELECTED,
          elementsType: elementTypes.item,
          isChosen
        })
      },
      handleItemOpenedUpdate({ id, opened }) {
        this.openedItems = {
          ...this.openedItems,
          [id]: opened
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  // stylelint-disable declaration-no-important
  .collapse-item-enter-active,
  .collapse-item-leave-active {
    transition: max-height .5s ease, opacity .5s ease;
    overflow: hidden;
  }

  .collapse-item-enter-from,
  .collapse-item-leave-to {
    max-height: 0;
    opacity: 0;
  }

  .collapse-item-enter-to,
  .collapse-item-leave-from {
    max-height: 300px;
    opacity: 1;
  }

  .summary-collection {
    min-height: 56px;
    font-size: .875rem;
    border-left: 7px solid transparent;
    background-color: $mi-white;
    box-shadow: 0 0 0 1px $mi-silver-300;
    max-height: calc(100vh - 390px);

    ::v-deep(.summary-collection__header) {
      position: sticky;
      top: 0;
      z-index: 1;
      background-color: $mi-white;
    }

    &__collapse-btn,
    &__clear-btn,
    ::v-deep(.search-summary__collection-action-btn),
    ::v-deep(.label--details) {
      transition: opacity .2s ease;
    }

    &__collapse-btn,
    &__clear-btn,
    ::v-deep(.search-summary__collection-action-btn) {
      display: none;
      pointer-events: none;
    }

    &__collapse-btn {
      margin-top: 2px;
      margin-right: 8px;
    }

    &__clear-btn {
      margin-top: 2px;
      margin-right: 8px;

      ::v-deep(.mi-icon) {
        margin-right: 0;
      }
    }

    &__clear-btn-disabled {
      ::v-deep(.mi-icon) {
        color: $mi-silver-400;
      }
    }

    &__label {
      flex-direction: row;
      justify-content: flex-start;
    }

    &__content {
      padding-left: .75rem;
      padding-right: .75rem;

      ::v-deep(.q-expansion-item) {
        border: 1px solid $mi-white;
      }

      ::v-deep(.q-item--dense) {
        padding-left: 8px !important;
      }

      ::v-deep(.q-item) {
        padding-left: 24px;
      }
    }

    ::v-deep(.summary-collection__expand-icon) .q-expansion-item__toggle-icon {
      font-size: 9px;
    }

    ::v-deep(.summary-collection__header:hover) {
      .summary-collection__collapse-btn,
      .summary-collection__clear-btn,
      .search-summary__collection-action-btn {
        display: inline;
        pointer-events: auto;
      }

      .label--details {
        display: none;
        pointer-events: none;
      }
    }
  }

  .summary-collection--yellow {
    border-left-color: #fbcd44;
  }

  .summary-collection--green {
    border-left-color: $mi-green-500;
  }
</style>
